<script setup lang="ts">
import { computed, watch } from 'vue';

import { useToast } from '@/shared/lib';
import { AppButton, AppToggle, IconMegaphone } from '@/shared/ui';

import { usePushNotificationPreferences } from '../model/use-push-notification-preferences';
import { usePushNotifications } from '../model/use-push-notifications';

const toast = useToast();
const pushNotifications = usePushNotifications();
const notificationPreferences = usePushNotificationPreferences();

const content = computed(() => {
  switch (pushNotifications.status.value) {
    case 'server-disabled':
      return {
        title: 'Push-уведомления пока не включены на сервере',
        description: 'Установка приложения работает. Уведомления появятся, когда push включат в конфигурации сервера.',
      };
    case 'unsupported':
      return {
        title: 'Уведомления недоступны',
        description: 'Этот браузер не поддерживает push-уведомления.',
      };
    case 'ios-requires-standalone':
      return {
        title: 'Откройте Mors с экрана «Домой»',
        description: 'Если значок Mors уже добавлен, откройте его. Если значка нет, перейдите к инструкции установки.',
      };
    case 'denied':
      return {
        title: 'Уведомления заблокированы',
        description: 'Разрешите уведомления для Mors в настройках браузера или устройства.',
      };
    case 'subscribed':
      return {
        title: 'Уведомления включены',
        description: 'Выберите события, о которых нужно сообщать на этом устройстве.',
      };
    case 'subscribing':
      return {
        title: 'Включаем уведомления',
        description: 'Подтвердите разрешение в системном диалоге браузера.',
      };
    case 'unsubscribing':
      return {
        title: 'Отключаем уведомления',
        description: 'Удаляем подписку этого устройства.',
      };
    case 'unclaimed':
      return {
        title: 'Подписку нужно включить заново',
        description: 'Прежняя локальная подписка больше не связана с текущим пользователем.',
      };
    case 'cleanup-failed':
      return {
        title: 'Не удалось удалить прежнюю подписку',
        description: 'Браузер сохранил подписку. Повторите отключение перед новым включением.',
      };
    case 'error':
      return {
        title: 'Не удалось обновить настройку',
        description: 'Проверьте подключение и повторите действие.',
      };
    default:
      return {
        title: 'Push-уведомления',
        description: 'Разрешите Mors сообщать о важных событиях на этом устройстве.',
      };
  }
});

const isBusy = computed(() => {
  return pushNotifications.status.value === 'subscribing' || pushNotifications.status.value === 'unsubscribing';
});

const canEnable = computed(() => {
  return (
    pushNotifications.status.value === 'default' ||
    pushNotifications.status.value === 'unclaimed' ||
    pushNotifications.status.value === 'error'
  );
});

const enable = async (): Promise<void> => {
  const outcome = await pushNotifications.enable();

  if (outcome === 'subscribed') {
    toast.show('Уведомления включены');
  } else if (outcome === 'denied') {
    toast.error('Разрешение на уведомления не предоставлено');
  } else if (outcome === 'dismissed') {
    toast.show('Уведомления можно включить позже');
  } else if (outcome === 'error') {
    toast.error('Не удалось включить уведомления');
  }
};

const disable = async (): Promise<void> => {
  const outcome = await pushNotifications.disable();

  if (outcome === 'disabled') {
    toast.show('Уведомления отключены на этом устройстве');
  } else if (outcome === 'server-cleanup-failed') {
    toast.error('На устройстве уведомления отключены. Серверную настройку не удалось обновить.');
  } else {
    toast.error('Не удалось отключить уведомления на устройстве');
  }
};

const sendTest = async (): Promise<void> => {
  const outcome = await pushNotifications.sendTest();

  if (outcome === 'sent') {
    toast.show('Тестовое уведомление отправлено');
  } else if (outcome === 'rate-limited') {
    toast.error('Слишком много тестов. Попробуйте через минуту.');
  } else if (outcome === 'not-subscribed') {
    toast.error('Сначала включите уведомления');
  } else {
    toast.error('Не удалось отправить тестовое уведомление');
  }
};

const updatePreference = async (
  kind: Parameters<typeof notificationPreferences.update>[0],
  enabled: boolean,
): Promise<void> => {
  const outcome = await notificationPreferences.update(kind, enabled);

  if (outcome === 'access-denied') {
    toast.error('Недостаточно прав, чтобы включить этот тип уведомлений');
  } else if (outcome === 'error') {
    toast.error('Не удалось обновить тип уведомлений');
  }
};

watch(
  () => pushNotifications.status.value,
  async (status) => {
    if (status === 'subscribed' && !(await notificationPreferences.load())) {
      toast.error('Не удалось загрузить типы уведомлений');
    }
  },
  { immediate: true },
);
</script>

<template>
  <section
    aria-labelledby="push-notifications-title"
    class="rounded-xl border border-(--border) bg-(--surface-card) p-4 md:p-6"
  >
    <div class="flex items-start gap-3">
      <span
        aria-hidden="true"
        class="flex size-10 shrink-0 items-center justify-center rounded-xl bg-(--surface-sunken) text-(--accent)"
      >
        <IconMegaphone
          :width="20"
          :height="20"
        />
      </span>
      <div class="min-w-0">
        <h2
          id="push-notifications-title"
          class="text-sm font-semibold text-(--text-primary)"
        >
          {{ content.title }}
        </h2>
        <p class="mt-1 text-sm leading-5 text-(--text-secondary)">
          {{ content.description }}
        </p>
      </div>
    </div>

    <div
      v-if="
        pushNotifications.status.value === 'subscribed' && notificationPreferences.visiblePreferences.value.length > 0
      "
      class="mt-5 border-t border-(--border) pt-5"
    >
      <h3 class="text-sm font-semibold text-(--text-primary)">Типы уведомлений</h3>
      <div class="mt-2 grid gap-2">
        <article
          v-for="preference in notificationPreferences.visiblePreferences.value"
          :key="preference.kind"
          class="flex min-h-16 items-center justify-between gap-4 rounded-lg bg-(--surface-sunken) px-3 py-2"
        >
          <div class="min-w-0">
            <p class="text-sm font-medium text-(--text-primary)">{{ preference.title }}</p>
            <p class="mt-0.5 text-xs leading-4 text-(--text-secondary)">
              {{ preference.description }}
            </p>
          </div>
          <AppToggle
            :model-value="preference.enabled"
            :disabled="notificationPreferences.isLoading.value || notificationPreferences.pendingKind.value !== null"
            :aria-label="`${preference.title}: ${preference.enabled ? 'включено' : 'выключено'}`"
            @update:model-value="updatePreference(preference.kind, $event)"
          />
        </article>
      </div>
    </div>

    <div
      v-if="pushNotifications.status.value === 'subscribed'"
      class="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2"
    >
      <AppButton
        outline
        class="min-h-11"
        :disabled="isBusy"
        @click="sendTest"
      >
        Отправить тест
      </AppButton>
      <AppButton
        outline
        class="min-h-11"
        @click="disable"
      >
        Отключить
      </AppButton>
    </div>

    <AppButton
      v-else-if="pushNotifications.status.value === 'cleanup-failed'"
      outline
      class="mt-4 min-h-11 w-full"
      @click="disable"
    >
      Повторить отключение
    </AppButton>

    <AppButton
      v-else-if="canEnable"
      class="mt-4 min-h-11 w-full"
      :loading="pushNotifications.status.value === 'subscribing'"
      @click="enable"
    >
      {{ pushNotifications.status.value === 'error' ? 'Повторить' : 'Включить уведомления' }}
    </AppButton>
  </section>
</template>
